import Spinners from "components/Shared/Spinners"
import { patch, post } from "helpers/api_helper"
import { NEW_USER_RESET_PASSWORD, VALIDATE_UUID } from "helpers/url_helper"
import React, { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Card, CardBody, Col, Container, Label, Row } from "reactstrap"
import { showAlert } from "store/reducers/alertReducer"
import AuthBrandingLogo from "components/Authentication/AuthBrandingLogo"
import { ROUTES } from "constants/routes"
import useAuthPageBranding from "hooks/useAuthPageBranding"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import {
  resetPasswordSchemaForUser,
  userResetPasswordSchema,
} from "components/Common/validation"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const TENANT_ID_SESSION_KEY = "TENANT_ID"

const ResetPassword = () => {
  const { t } = useTranslation()
  const { brandTitle, authPageClassName } = useAuthPageBranding(
    t("Reset password")
  )

  const [loading, setLoading] = useState(true)
  const [uuidValidateInfo, setUuidValidateInfo] = useState({})

  const { uuid } = useParams()
  const [searchParams] = useSearchParams()
  const tenantIdFromUrl = searchParams.get("tenantId")
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    if (!tenantIdFromUrl) {
      return undefined
    }

    sessionStorage.setItem(TENANT_ID_SESSION_KEY, tenantIdFromUrl)
    return () => {
      sessionStorage.removeItem(TENANT_ID_SESSION_KEY)
    }
  }, [tenantIdFromUrl])

  useEffect(() => {
    uuid && validateUuid()
  }, [uuid, tenantIdFromUrl])

  const validateUuid = async () => {
    const payload = {
      uuid,
    }
    const result = await post(VALIDATE_UUID, payload)
    if (result) {
      setLoading(false)
      if (result?.statusCode === 200 && result?.status) {
        setUuidValidateInfo(result.data)
      } else {
        dispatch(showAlert({ text: result.msg, type: "error" }))
        navigate(ROUTES.LOGIN)
      }
    }
  }

  const handleNewPassword = async (values, { resetForm }) => {
    const { newPassword, confirmPassword } = values
    const payload = {
      id: uuidValidateInfo._id,
      newPassword,
      confirmPassword,
      uuid,
    }
    const result = await patch(NEW_USER_RESET_PASSWORD, payload)
    if (result) {
      if (result?.statusCode === 200 && result?.status) {
        dispatch(showAlert({ text: result.msg, type: "success" }))
        navigate(ROUTES.LOGIN)
      } else {
        dispatch(showAlert({ text: result.msg, type: "error" }))
      }
    }
  }

  if (loading) return <Spinners />
  return (
    <div>
      <div
        className={`account-pages d-flex align-items-center pt-sm-5 ${authPageClassName}`}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div
                  className="login-platform-hero"
                >
                  <Row>
                    <Col xs={12}>
                      <div
                        className="p-4"
                      >
                        <h5>
                          {brandTitle}
                          <span className="d-block small mt-1">
                            {t("Welcome")}{" "}
                            {uuidValidateInfo.firstName +
                              " " +
                              uuidValidateInfo.lastName}
                          </span>
                        </h5>
                        <p>{t("Please reset your password")}</p>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-4">
                  <AuthBrandingLogo className="login-platform-logo mb-3" />
                  <div className="p-2">
                    <Formik
                      initialValues={{ newPassword: "", confirmPassword: "" }}
                      validationSchema={resetPasswordSchemaForUser}
                      onSubmit={handleNewPassword}
                    >
                      {({ values, errors, touched, isSubmitting }) => (
                        <Form className="form-horizontal">
                          {/* {console.log(errors)} */}
                          <div className="mb-3">
                            <Label className="form-label">
                              {t("New Password")}
                            </Label>
                            <div className="position-relative">
                              <Field
                                name="newPassword"
                                className={`form-control ${
                                  touched.newPassword &&
                                  errors.newPassword &&
                                  "is-invalid"
                                }`}
                                placeholder={t("New Password")}
                                type={showPassword ? "text" : "password"}
                              />
                              <i
                                onClick={() => setShowPassword(!showPassword)}
                                className={`bx bx-${
                                  showPassword ? "show-alt" : "hide"
                                } common_eye`}
                              ></i>
                            </div>
                            <TextError name="newPassword" />
                          </div>
                          <div className="mb-3">
                            <Label className="form-label">
                              {t("Confirm Password")}
                            </Label>
                            <div className="position-relative">
                              <Field
                                name="confirmPassword"
                                className={`form-control ${
                                  touched.confirmPassword &&
                                  errors.confirmPassword &&
                                  "is-invalid"
                                }`}
                                placeholder={t("Confirm Password")}
                                type={showConfirmPassword ? "text" : "password"}
                              />
                              <i
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                className={`bx bx-${
                                  showConfirmPassword ? "show-alt" : "hide"
                                } common_eye`}
                              ></i>
                            </div>
                            <TextError name="confirmPassword" />
                          </div>
                          <Row className="">
                            <Col className="text-end">
                              <button
                                className="btn btn-primary w-md "
                                type="submit"
                                disabled={isSubmitting}
                              >
                                {t("Save")}
                              </button>
                            </Col>
                          </Row>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center login-platform-footer">
                <p>
                  {t("Go back to")}{" "}
                  <Link
                    to={ROUTES.LOGIN}
                    className="font-weight-medium text-primary"
                  >
                    {t("Login")}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  )
}

export default ResetPassword
